/**
 * Feature Tier Service
 *
 * Manages the 4-tier feature visibility model for progressive rollout:
 * - GA: General Availability - all users
 * - Early Access: Select customers testing new features
 * - Experimental: Early adopters with cutting-edge features
 * - Dev: Development environment or RP internal staff only
 *
 * RP internal staff (rp_admin/rp_support) bypass all tier gating.
 *
 * @see /docs/ux-concepts/CONNECTIONS-STRATEGY-SUMMARY.md
 */

import type { User } from '@/types/milestone.types'
import type { FeatureTier } from '@/types/featureTier.types'
import { stackService } from './stackService'

/**
 * Tier hierarchy (higher index = more access)
 */
const TIER_HIERARCHY: FeatureTier[] = ['GA', 'Early Access', 'Experimental', 'Dev']

/**
 * Check if user has access to a feature at the given tier
 */
export function hasFeatureAccess(
  user: User | null | undefined,
  requiredTier: FeatureTier
): boolean {
  if (!user) return false

  // RP internal staff (rp_admin/rp_support) bypass all tier gating
  if (hasInternalAccess(user)) {
    return true
  }

  // Get user's effective tier from their current organization
  const userTier = getEffectiveTier(user)

  // Check if user's tier >= required tier
  const userTierIndex = TIER_HIERARCHY.indexOf(userTier)
  const requiredTierIndex = TIER_HIERARCHY.indexOf(requiredTier)

  return userTierIndex >= requiredTierIndex
}

/**
 * Check if user has RP internal access (rp_admin or rp_support)
 */
export function hasInternalAccess(user: User | null | undefined): boolean {
  if (!user?.globalRoles) return false
  return user.globalRoles.includes('rp_admin') || user.globalRoles.includes('rp_support')
}

/**
 * Check if running in development environment
 */
export function isDevelopmentEnvironment(): boolean {
  // Check hostname
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'localhost') return true
    if (window.location.hostname.includes('-dev.rocketpricing.com')) return true
  }

  // Check selected stack (dev stacks = development)
  const stack = stackService.getSelectedStack()
  return ['jbailey', 'sbass', 'dev'].includes(stack)
}

/**
 * Get user's effective tier based on current organization
 */
export function getEffectiveTier(user: User | null | undefined): FeatureTier {
  if (!user) return 'GA'

  // RP internal staff get Dev tier (bypass all tier gating)
  if (hasInternalAccess(user)) {
    return 'Dev'
  }

  // Read from plan.feature_tier if available
  if (user.plan && typeof user.plan === 'object' && user.plan.feature_tier) {
    const tier = user.plan.feature_tier as FeatureTier
    // Validate it's a known tier
    if (TIER_HIERARCHY.includes(tier)) {
      return tier
    }
  }

  // Default to GA for users without a feature_tier in their plan
  return 'GA'
}

/**
 * Check if user can see Settings dropdown
 * Requires: Org Owner/Admin OR rp_admin/rp_support
 */
export function canAccessSettings(user: User | null | undefined): boolean {
  if (!user) return false

  // RP internal staff always have access
  if (hasInternalAccess(user)) {
    return true
  }

  // Check tenant role in current organization
  const orgRole = user.currentOrganization?.role
  return orgRole === 'admin' // 'admin' role = owner or admin
}

/**
 * Check if user can see Dev dropdown
 * Requires: Dev environment OR rp_admin/rp_support
 */
export function canAccessDevTools(user: User | null | undefined): boolean {
  if (!user) return false

  // RP internal staff always have access
  if (hasInternalAccess(user)) {
    return true
  }

  // Dev environment only
  return isDevelopmentEnvironment()
}

/**
 * Filter menu items by user's tier access
 */
export function filterByTier<T extends { tier: FeatureTier }>(
  items: T[],
  user: User | null | undefined
): T[] {
  return items.filter(item => hasFeatureAccess(user, item.tier))
}

export const featureTierService = {
  hasFeatureAccess,
  hasInternalAccess,
  isDevelopmentEnvironment,
  getEffectiveTier,
  canAccessSettings,
  canAccessDevTools,
  filterByTier,
}
